import { Draft, PayloadAction, createSlice } from '@reduxjs/toolkit';

import { ActiveFeatureProperties, FocusOnExtent, InteractionType, MapMode, MapState } from 'models';

import { Layer, MapModeEnum } from 'enums';

import { Coordinate } from 'ol/coordinate';
import { Extent } from 'ol/extent';

const initialState: MapState = {
  visibleBackgroundLayers: [Layer.TOPOWEB],
  visibleForegroundLayers: [
    Layer.PUBLICERADE_AKTIVITETER_SYMBOLER_WMTS,
    Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS
  ],
  hoveredId: null,
  mapMode: MapModeEnum.DEFAULT,
  activeInteractions: [],
  totalDrawnGeometries: 0,
  clickedActiveFeatures: undefined,
  cqlBebyggelseLayerWMS: '',
  visibleMapExtent: undefined
};

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setCqlBebyggelseLayerWMS: (state: Draft<MapState>, payloadAction: PayloadAction<string>): void => {
      state.cqlBebyggelseLayerWMS = payloadAction.payload;
    },
    setTotalDrawnGeometries: (state: Draft<MapState>, payloadAction: PayloadAction<number>) => {
      state.totalDrawnGeometries = payloadAction.payload;
    },
    setActiveInteraction: (
      state: Draft<MapState>,
      payloadAction: PayloadAction<InteractionType | InteractionType[]>
    ) => {
      state.activeInteractions = Array.isArray(payloadAction.payload) ? payloadAction.payload : [payloadAction.payload];
    },
    setMapMode: (state: Draft<MapState>, payloadAction: PayloadAction<MapMode>) => {
      state.mapMode = payloadAction.payload;
    },
    setVisibleBackgroundLayers: (state: Draft<MapState>, payloadAction: PayloadAction<Layer[]>) => {
      state.visibleBackgroundLayers = payloadAction.payload;
    },
    setVisibleForegroundLayers: (state: Draft<MapState>, payloadAction: PayloadAction<Layer[]>) => {
      state.visibleForegroundLayers = payloadAction.payload;
    },
    setHoveredId: (state: Draft<MapState>, payloadAction: PayloadAction<string>) => {
      state.hoveredId = payloadAction.payload;
    },
    setFocusOnExtent: (state: Draft<MapState>, payloadAction: PayloadAction<FocusOnExtent | undefined>) => {
      state.focusOnExtent = payloadAction.payload;
    },
    setClickedActiveFeatures: (
      state: Draft<MapState>,
      payloadAction: PayloadAction<{ activeFeatures: ActiveFeatureProperties[]; activeFeaturesRequestedAt: Coordinate }>
    ) => {
      state.clickedActiveFeatures = payloadAction.payload;
    },
    clearClickedActiveFeatures: (state: Draft<MapState>) => {
      state.clickedActiveFeatures = undefined;
    },
    setVisibleMapExtent: (state: Draft<MapState>, payloadAction: PayloadAction<Extent>) => {
      state.visibleMapExtent = payloadAction.payload;
    }
  }
});

export const {
  setVisibleBackgroundLayers,
  setVisibleForegroundLayers,
  setHoveredId,
  setFocusOnExtent,
  setMapMode,
  setActiveInteraction,
  setTotalDrawnGeometries,
  setClickedActiveFeatures,
  clearClickedActiveFeatures,
  setCqlBebyggelseLayerWMS,
  setVisibleMapExtent
} = mapSlice.actions;

export default mapSlice.reducer;
